import * as React from 'react'

import { CheckIcon, HamburgerMenuIcon, TableIcon } from '@radix-ui/react-icons'
import { PopoverPortal } from '@radix-ui/react-popover'
import { Button } from '~/sotano-kit/primitives/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/sotano-kit/primitives/popover'
import { Input } from '~/sotano-kit/primitives/input'
import {
  AtLeast,
  ListBlock,
  ListView,
  DEFAULT_LIST_VIEW,
} from '@basehub/data/replicache/index'
import { useSelectedView } from './use-selected-view'
import { clsx } from 'clsx'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { getId } from '@basehub/utils/id/index'
import { getViewDefaultTitle } from '@basehub/data/replicache/schema/block/list/utils'
import { Position_Array } from '@basehub/utils/array/insert-into-array'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { useEditorMode } from '../../../editor-mode-provider'

export const ListBlockViewSwitcher = ({
  block,
  path,
  position = { type: 'first' },
  children,
  onCreateNewView,
}: {
  block: ListBlock
  path: string
  position: Position_Array
  children: (selectedView?: ListView) => React.ReactNode
  onCreateNewView?: (view: ListView) => void
}) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const [selectedView, updateSelectedView] = useSelectedView(block)
  const [viewInputValue, setViewInputValue] = React.useState('')
  const [hoveredIndex, setHoveredIndex] = React.useState(0)
  const replicache = useRepoReplicache().data?.replicache
  const treeManager = useWorkingTreeStore((s) => s.tree)
  const editorMode = useEditorMode()

  const views = block.value.views?.length
    ? block.value.views
    : [DEFAULT_LIST_VIEW]
  const filteredViews = views.filter((view) =>
    view.title.toLowerCase().includes(viewInputValue.toLowerCase())
  )
  const lastViewIndex = viewInputValue
    ? filteredViews.length
    : filteredViews.length - 1

  const inferredLayoutFromQuery: ListView['layout'] = React.useMemo(() => {
    if (
      'gallery'.includes(viewInputValue.toLowerCase()) ||
      viewInputValue.toLowerCase().includes('gallery') ||
      viewInputValue.toLowerCase().includes('grid') ||
      viewInputValue.toLowerCase().includes('photo') ||
      viewInputValue.toLowerCase().includes('picture')
    ) {
      return 'gallery'
    } else if (
      'inline'.includes(viewInputValue.toLowerCase()) ||
      viewInputValue.toLowerCase().includes('inline')
    ) {
      return 'inline'
    }

    return 'table'
  }, [viewInputValue])

  return (
    <Popover
      open={isOpen}
      onOpenChange={() => {
        setIsOpen((prev) => {
          if (!prev) {
            setViewInputValue('')
            setHoveredIndex(0)
          }

          return !prev
        })
      }}
    >
      <PopoverTrigger
        className="flex group items-center cursor-default outline-dark-control-base focus:outline focus:outline-1 focus-visible:outline focus-visible:outline-1 rounded-md"
        aria-label="Open list view switcher"
        tabIndex={0}
      >
        {children(selectedView)}
      </PopoverTrigger>
      <PopoverPortal>
        <PopoverContent
          className="tex-sm shadow-lg z-40 mx-5 w-[272px] border border-dark-border-solid !bg-dark-base shadow-modal !p-0"
          sideOffset={5}
        >
          <div className="p-2">
            <Input
              size="sm"
              placeholder={
                editorMode !== 'write:all'
                  ? 'Find view...'
                  : 'Find or create view...'
              }
              onKeyDown={async (e) => {
                if (!replicache || !treeManager) return

                if (e.key === 'Escape') {
                  setIsOpen(false)
                  return
                }

                if (e.key === 'ArrowUp') {
                  if (hoveredIndex === 0) return

                  setHoveredIndex((prevState) => {
                    if (prevState > lastViewIndex) return lastViewIndex

                    return prevState - 1
                  })

                  return
                }

                if (e.key === 'ArrowDown') {
                  if (hoveredIndex === lastViewIndex) return

                  setHoveredIndex((prevState) => prevState + 1)
                  return
                }

                if (e.key === 'Enter') {
                  const hoveredView = filteredViews[hoveredIndex]

                  if (hoveredView) {
                    await updateSelectedView(hoveredView.id)
                  } else if (viewInputValue) {
                    replicache?.mutate
                      .createListView({
                        path,
                        blockId: block.id,
                        targetBlockId_withinTree: treeManager.getNode(
                          block.typeOptions.targetBlockId,
                          { required: true }
                        ).value.id,
                        createDefaultViewIfNotExists: {
                          id: getId(),
                          layout: inferredLayoutFromQuery,
                        },
                        data: { title: viewInputValue || 'New view' },
                        position,
                        id: getId(),
                        isoNow: new Date().toISOString(),
                      })
                      .then(({ view: newView }) => {
                        updateSelectedView(newView.id)
                        onCreateNewView?.(newView)
                        setIsOpen(false)
                      })
                  }

                  setIsOpen(false)
                }
              }}
              value={viewInputValue}
              onChange={(e) => setViewInputValue(e.target.value)}
              type="text"
            />
          </div>

          <hr className="border-dark-border-solid -mx-2" />
          <div className="p-1.5">
            {filteredViews.map((view, index) => {
              const isActive =
                view.id === (selectedView?.id ?? DEFAULT_LIST_VIEW.id)
              const isHovered = hoveredIndex === index

              return (
                <Button
                  key={view.id}
                  unstyled
                  focusStyled
                  onMouseEnter={() => setHoveredIndex(index)}
                  className={clsx(
                    'flex items-center justify-between rounded-md overflow-hidden px-2 py-2 text-xs w-full cursor-default h-8',
                    isHovered && 'bg-dark-shade-hover'
                  )}
                  onClick={async () => {
                    await updateSelectedView(view.id)
                    setIsOpen(false)
                  }}
                >
                  <span className="inline-flex items-center justify-center gap-x-2 overflow-hidden">
                    <ListBlockViewIcon
                      view={view}
                      iconProps={{
                        width: 15,
                        height: 15,
                        className: 'shrink-0',
                      }}
                    />
                    <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                      {view.title || getViewDefaultTitle(view)}
                    </span>
                  </span>
                  {isActive && <CheckIcon />}
                </Button>
              )
            })}

            {/* Empty state (only for readonly) */}
            {!filteredViews.length && editorMode !== 'write:all' && (
              <span className="text-dark-dim text-sm px-2">
                No results for {viewInputValue}
              </span>
            )}

            {viewInputValue && editorMode === 'write:all' && (
              <Button
                className={clsx(
                  'flex items-center justify-between rounded overflow-hidden px-2 py-2 text-xs w-full h-8',
                  hoveredIndex >= lastViewIndex && 'bg-dark-shade-hover'
                )}
                onClick={async () => {
                  if (!replicache || !treeManager) return

                  replicache.mutate
                    .createListView({
                      path,
                      blockId: block.id,
                      targetBlockId_withinTree: treeManager.getNode(
                        block.typeOptions.targetBlockId,
                        { required: true }
                      ).value.id,
                      createDefaultViewIfNotExists: {
                        id: getId(),
                        layout: 'table',
                      },
                      data: { title: viewInputValue ?? 'New view' },
                      id: getId(),
                      position,
                      isoNow: new Date().toISOString(),
                    })
                    .then(({ view: newView }) => {
                      updateSelectedView(newView.id)
                      onCreateNewView?.(newView)
                      setIsOpen(false)
                    })
                }}
                unstyled
                onMouseEnter={() => setHoveredIndex(filteredViews.length + 1)}
              >
                <span className="inline-flex items-center justify-center gap-x-2 overflow-hidden">
                  <ListBlockViewIcon
                    view={{ layout: inferredLayoutFromQuery }}
                    iconProps={{ className: 'shrink-0' }}
                  />
                  <span className="overflow-hidden text-ellipsis whitespace-nowrap text-dark-muted">
                    Create view{' '}
                    <span className="text-dark-bright">{viewInputValue}</span>
                  </span>
                </span>
              </Button>
            )}
          </div>
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  )
}

export const ListBlockViewIcon = ({
  view,
  iconProps,
}: {
  view: AtLeast<ListView, 'layout'>
  iconProps?: React.ComponentPropsWithRef<typeof TableIcon>
}) => {
  switch (view.layout) {
    case 'table': {
      return <TableIcon {...iconProps} />
    }
    case 'gallery': {
      return <GalleryIcon {...iconProps} />
    }
    case 'inline': {
      return <HamburgerMenuIcon {...iconProps} />
    }
  }
}

const GalleryIcon = ({
  width = 15,
  height = 15,
  ...iconProps
}: React.ComponentPropsWithRef<typeof TableIcon>) => {
  return (
    <svg
      {...iconProps}
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid meet"
      width={width}
      height={height}
      viewBox="0 0 17 17"
      fill="none"
    >
      <path
        fill="currentColor"
        fillRule="evenodd"
        d="M15 2.4H3a.6.6 0 0 0-.6.6v12a.6.6 0 0 0 .6.6h12a.6.6 0 0 0 .6-.6V3a.6.6 0 0 0-.6-.6ZM3 1.2A1.8 1.8 0 0 0 1.2 3v12A1.8 1.8 0 0 0 3 16.8h12a1.8 1.8 0 0 0 1.8-1.8V3A1.8 1.8 0 0 0 15 1.2H3Z"
        clipRule="evenodd"
      />
      <path
        fill="currentColor"
        fillRule="evenodd"
        d="M2.4 10.8h13.2V12H2.4v-1.2Z"
        clipRule="evenodd"
      />
    </svg>
  )
}
